import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity,
  ArrowRight,
  BarChart3,
  Building2,
  Clipboard,
  Download,
  FileText,
  Handshake,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  Trash2,
  TrendingUp,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { Json } from "@/integrations/supabase/types";
import { verdictShort } from "@/lib/verdict-display";
import { useWorkspace } from "@/hooks/use-workspace";
import { isSuperadminWorkspace } from "@/lib/superadmin-workspace";

interface DealRow {
  id: string;
  context: Json;
  status: string;
  workspace_id: string;
  deal_type_id: string | null;
  preliminary_result: Json | null;
  final_result: Json | null;
  created_at: string;
  updated_at: string;
}

interface VersionRow {
  deal_id: string;
  deal_score: number | null;
  verdict: string | null;
  dscr: number | null;
}

interface SavedReport {
  id: string;
  label: string;
  report_type: string;
  deal_id: string | null;
  created_at: string;
  ai_content: string | null;
}

type CanonicalVerdict = "GO" | "MAYBE" | "NO-GO" | null;

const verdictPalette: Record<Exclude<CanonicalVerdict, null> | "Draft", { label: string; color: string; soft: string; Icon: typeof ShieldCheck }> = {
  GO: {
    label: verdictShort("GO"),
    color: "hsl(142 71% 45%)",
    soft: "hsl(142 71% 45% / 0.12)",
    Icon: ShieldCheck,
  },
  MAYBE: {
    label: verdictShort("MAYBE"),
    color: "hsl(38 92% 50%)",
    soft: "hsl(38 92% 50% / 0.12)",
    Icon: ShieldAlert,
  },
  "NO-GO": {
    label: verdictShort("NO-GO"),
    color: "hsl(0 84% 60%)",
    soft: "hsl(0 84% 60% / 0.12)",
    Icon: ShieldX,
  },
  Draft: {
    label: "Draft",
    color: "hsl(var(--muted-foreground))",
    soft: "hsl(var(--muted))",
    Icon: FileText,
  },
};

function asRecord(value: Json | null): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeVerdict(raw: unknown): CanonicalVerdict {
  if (!raw) return null;
  const value = String(raw).trim().toUpperCase().replace(/[\s-]+/g, "_");
  if (value === "GO" || value === "STRONG_GO" || value === "PROCEED") return "GO";
  if (
    value === "MAYBE" ||
    value === "CAUTION" ||
    value === "PROCEED_WITH_CAUTION" ||
    value === "PROCEED_WITH_FOCUS" ||
    value.includes("CAUTION")
  ) return "MAYBE";
  if (
    value === "NO" ||
    value === "NO_GO" ||
    value === "PASS" ||
    value === "PASS_ON_DEAL" ||
    value === "REJECT" ||
    value === "DECLINE"
  ) return "NO-GO";
  return null;
}

function getScore(deal: DealRow, version?: VersionRow): number | null {
  const finalResult = asRecord(deal.final_result);
  const preliminaryResult = asRecord(deal.preliminary_result);

  return (
    asNumber(version?.deal_score) ??
    asNumber(finalResult?.overall_score) ??
    asNumber(preliminaryResult?.overall_score)
  );
}

function getDscr(deal: DealRow, version?: VersionRow): number | null {
  const finalResult = asRecord(deal.final_result);
  const preliminaryResult = asRecord(deal.preliminary_result);
  const finalMetrics = asRecord((finalResult?.metrics as Json | undefined) ?? null);
  const preliminaryMetrics = asRecord((preliminaryResult?.metrics as Json | undefined) ?? null);

  return (
    asNumber(version?.dscr) ??
    asNumber(finalMetrics?.dscr) ??
    asNumber(finalResult?.dscr) ??
    asNumber(preliminaryMetrics?.dscr) ??
    asNumber(preliminaryResult?.dscr)
  );
}

function getVerdict(deal: DealRow, version?: VersionRow): CanonicalVerdict {
  const finalResult = asRecord(deal.final_result);
  const preliminaryResult = asRecord(deal.preliminary_result);

  return (
    normalizeVerdict(version?.verdict) ??
    normalizeVerdict(finalResult?.verdict) ??
    normalizeVerdict(finalResult?.verdict_label) ??
    normalizeVerdict(preliminaryResult?.verdict) ??
    normalizeVerdict(preliminaryResult?.verdict_label)
  );
}

function getUnitCount(deal: DealRow): number {
  const context = asRecord(deal.context);
  const rentRollFile = asRecord((context?.rent_roll_file as Json | undefined) ?? null);
  const rentRollExtracted = asRecord((rentRollFile?.extracted_data as Json | undefined) ?? null);
  const rentRollSummary = asRecord((rentRollExtracted?.summary as Json | undefined) ?? null);
  const offeringMemoFile = asRecord((context?.offering_memo_file as Json | undefined) ?? null);
  const offeringMemoExtracted = asRecord((offeringMemoFile?.extracted_data as Json | undefined) ?? null);
  const offeringMemoSummary = asRecord((offeringMemoExtracted?.summary as Json | undefined) ?? null);

  return (
    asNumber(context?.unit_count) ??
    asNumber(context?.total_units) ??
    asNumber(context?.units_total) ??
    asNumber(context?.number_of_units) ??
    asNumber(rentRollSummary?.total_units) ??
    asNumber(rentRollExtracted?.total_units) ??
    asNumber(offeringMemoSummary?.total_units) ??
    asNumber(offeringMemoExtracted?.total_units) ??
    0
  );
}

function formatWhole(value: number | null): string {
  if (value === null) return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value);
}

function formatDecimal(value: number | null, digits = 2): string {
  if (value === null) return "—";
  return value.toFixed(digits);
}

function formatPercent(value: number | null): string {
  if (value === null) return "—";
  return `${Math.round(value)}%`;
}

function SnapshotCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof BarChart3;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <Card className="border-border/60 bg-card/80 shadow-sm">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          <span className="eyebrow">{label}</span>
        </div>
        <div className="mt-3 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display text-3xl sm:text-[2rem] font-semibold leading-none text-foreground">
              {value}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const DealsV2Reports = () => {
  const navigate = useNavigate();
  const { activeWorkspaceId, loading: workspaceLoading } = useWorkspace();
  const [deals, setDeals] = useState<DealRow[]>([]);
  const [versionByDeal, setVersionByDeal] = useState<Record<string, VersionRow>>({});
  const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (workspaceLoading) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        navigate("/auth");
        return;
      }

      if (!activeWorkspaceId) {
        if (!cancelled) {
          setDeals([]);
          setVersionByDeal({});
          setSavedReports([]);
          setLoading(false);
        }
        return;
      }

      const superadmin = await isSuperadminWorkspace(activeWorkspaceId);
      let dealsQuery = supabase
        .from("deals")
        .select("id, context, status, workspace_id, deal_type_id, preliminary_result, final_result, created_at, updated_at")
        .not("deal_type_id", "is", null)
        .order("updated_at", { ascending: false });

      if (!superadmin) {
        dealsQuery = dealsQuery.eq("workspace_id", activeWorkspaceId);
      }

      const { data: dealRows, error: dealsError } = await dealsQuery;
      if (dealsError) {
        console.error(dealsError);
        if (!cancelled) setLoading(false);
        return;
      }

      const v2Deals = (dealRows || []) as DealRow[];
      const ids = v2Deals.map((deal) => deal.id);

      const [versionsRes, reportsRes] = await Promise.all([
        ids.length > 0
          ? supabase
              .from("deal_versions")
              .select("deal_id, deal_score, verdict, dscr, created_at")
              .in("deal_id", ids)
              .order("created_at", { ascending: false })
          : Promise.resolve({ data: [] as Array<VersionRow & { created_at?: string }>, error: null }),
        ids.length > 0
          ? (supabase
              .from("saved_reports" as any)
              .select("id, label, report_type, deal_id, created_at, ai_content")
              .in("deal_id", ids)
              .order("created_at", { ascending: false }) as any)
          : Promise.resolve({ data: [] as SavedReport[], error: null }),
      ]);

      const nextVersionMap: Record<string, VersionRow> = {};
      for (const row of ((versionsRes as { data?: Array<VersionRow & { created_at?: string }> }).data || [])) {
        if (!nextVersionMap[row.deal_id]) {
          nextVersionMap[row.deal_id] = {
            deal_id: row.deal_id,
            deal_score: row.deal_score,
            verdict: row.verdict,
            dscr: row.dscr,
          };
        }
      }

      if (!cancelled) {
        setDeals(v2Deals);
        setVersionByDeal(nextVersionMap);
        setSavedReports((((reportsRes as { data?: SavedReport[] }).data) || []) as SavedReport[]);
        setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [activeWorkspaceId, navigate, workspaceLoading]);

  const deleteReport = async (id: string) => {
    const { error } = await (supabase.from("saved_reports" as any).delete().eq("id", id) as any);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }

    setSavedReports((prev) => prev.filter((report) => report.id !== id));
    toast({ title: "Report deleted" });
  };

  const analytics = useMemo(() => {
    const scoredDeals = deals.filter((deal) => getScore(deal, versionByDeal[deal.id]) !== null);
    const evaluatedDeals = deals.filter((deal) => {
      const version = versionByDeal[deal.id];
      return getScore(deal, version) !== null || getVerdict(deal, version) !== null || getDscr(deal, version) !== null;
    });

    const scores = scoredDeals
      .map((deal) => getScore(deal, versionByDeal[deal.id]))
      .filter((score): score is number => typeof score === "number");

    const dscrs = deals
      .map((deal) => getDscr(deal, versionByDeal[deal.id]))
      .filter((dscr): dscr is number => typeof dscr === "number");

    const verdictCounts: Record<"GO" | "MAYBE" | "NO-GO" | "Draft", number> = {
      GO: 0,
      MAYBE: 0,
      "NO-GO": 0,
      Draft: 0,
    };

    deals.forEach((deal) => {
      const verdict = getVerdict(deal, versionByDeal[deal.id]);
      if (verdict) verdictCounts[verdict] += 1;
      else verdictCounts.Draft += 1;
    });

    const totalUnits = deals.reduce((sum, deal) => sum + getUnitCount(deal), 0);
    const avgScore = scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null;
    const avgDscr = dscrs.length ? dscrs.reduce((sum, dscr) => sum + dscr, 0) / dscrs.length : null;
    const goRate = evaluatedDeals.length ? (verdictCounts.GO / evaluatedDeals.length) * 100 : null;
    const dscrBelowOneRate = dscrs.length ? (dscrs.filter((dscr) => dscr < 1).length / dscrs.length) * 100 : null;

    const activityMap = new Map<string, { label: string; count: number; sortKey: number }>();
    deals.forEach((deal) => {
      const date = new Date(deal.created_at);
      const label = date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const current = activityMap.get(key);
      activityMap.set(key, {
        label,
        count: (current?.count || 0) + 1,
        sortKey: new Date(date.getFullYear(), date.getMonth(), 1).getTime(),
      });
    });

    const monthlyData = [...activityMap.values()]
      .sort((a, b) => a.sortKey - b.sortKey)
      .slice(-6);

    const scoreBuckets = [
      { range: "0–20", min: 0, max: 20 },
      { range: "21–40", min: 21, max: 40 },
      { range: "41–60", min: 41, max: 60 },
      { range: "61–80", min: 61, max: 80 },
      { range: "81–100", min: 81, max: 100 },
    ].map((bucket) => ({
      ...bucket,
      count: scores.filter((score) => score >= bucket.min && score <= bucket.max).length,
    }));

    return {
      totalDeals: deals.length,
      evaluatedDeals: evaluatedDeals.length,
      totalUnits,
      avgScore,
      avgDscr,
      goRate,
      dscrBelowOneRate,
      verdictCounts,
      monthlyData,
      scoreBuckets,
      maxMonthlyCount: Math.max(1, ...monthlyData.map((month) => month.count)),
      maxBucketCount: Math.max(1, ...scoreBuckets.map((bucket) => bucket.count)),
    };
  }, [deals, versionByDeal]);

  if (loading || workspaceLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 py-8 sm:px-6 sm:py-12">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <BarChart3 className="h-4 w-4 text-accent" />
            <span className="eyebrow">Analytics · V2 Workspace</span>
          </div>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-1">
              <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Reports</h1>
              <p className="max-w-2xl text-sm text-muted-foreground">
                Clean portfolio reporting across the active workspace, using the latest V2 score, verdict, DSCR, and unit data from current deals.
              </p>
            </div>
            <div className="rounded-lg border border-border/60 bg-card/70 px-4 py-3 text-sm text-muted-foreground">
              <span className="eyebrow text-foreground">Coverage</span>
              <div className="mt-1 font-display text-2xl text-foreground">{analytics.evaluatedDeals}/{analytics.totalDeals}</div>
              <p className="text-xs">Deals with enough scoring data to include in workspace averages.</p>
            </div>
          </div>
        </motion.div>

        <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <SnapshotCard
              icon={BarChart3}
              label="Total Deals"
              value={formatWhole(analytics.totalDeals)}
              hint="All V2 deals in the active workspace"
            />
            <SnapshotCard
              icon={TrendingUp}
              label="Avg Score"
              value={formatWhole(analytics.avgScore !== null ? Math.round(analytics.avgScore) : null)}
              hint="Latest scored result across workspace deals"
            />
            <SnapshotCard
              icon={Activity}
              label="Avg DSCR"
              value={analytics.avgDscr !== null ? `${formatDecimal(analytics.avgDscr)}x` : "—"}
              hint="Coverage from current scored underwriting"
            />
            <SnapshotCard
              icon={Building2}
              label="Total Units"
              value={formatWhole(analytics.totalUnits)}
              hint="Parsed from stored V2 deal context"
            />
            <SnapshotCard
              icon={Handshake}
              label="GO Rate"
              value={formatPercent(analytics.goRate)}
              hint="Share of evaluated deals marked GO"
            />
          </div>
        </motion.section>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.35fr_0.95fr]">
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-border/60 bg-card/80 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="font-display text-xl text-foreground">Portfolio mix</CardTitle>
                <p className="text-sm text-muted-foreground">Workspace-wide verdict breakdown based on the latest saved V2 outcomes.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {(["GO", "MAYBE", "NO-GO", "Draft"] as const).map((key) => {
                  const meta = verdictPalette[key];
                  const count = analytics.verdictCounts[key];
                  const share = analytics.totalDeals > 0 ? (count / analytics.totalDeals) * 100 : 0;
                  const Icon = meta.Icon;

                  return (
                    <div key={key} className="space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="flex h-7 w-7 items-center justify-center rounded-md" style={{ backgroundColor: meta.soft }}>
                            <Icon className="h-3.5 w-3.5" style={{ color: meta.color }} />
                          </div>
                          <div>
                            <div className="font-mono text-[11px] uppercase tracking-[0.16em]" style={{ color: meta.color }}>
                              {meta.label}
                            </div>
                            <p className="text-xs text-muted-foreground">{formatPercent(share)} of workspace deals</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-display text-2xl text-foreground">{count}</div>
                          <p className="text-xs text-muted-foreground">deal{count === 1 ? "" : "s"}</p>
                        </div>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${share}%`, backgroundColor: meta.color }}
                        />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="border-border/60 bg-card/80 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="font-display text-xl text-foreground">Score distribution</CardTitle>
                <p className="text-sm text-muted-foreground">How current deal scores are spread across the workspace.</p>
              </CardHeader>
              <CardContent>
                {analytics.scoreBuckets.some((bucket) => bucket.count > 0) ? (
                  <div className="space-y-3">
                    {analytics.scoreBuckets.map((bucket) => {
                      const width = (bucket.count / analytics.maxBucketCount) * 100;
                      return (
                        <div key={bucket.range} className="grid grid-cols-[68px_1fr_36px] items-center gap-3">
                          <span className="eyebrow text-muted-foreground">{bucket.range}</span>
                          <div className="h-8 overflow-hidden rounded-md bg-muted">
                            <div
                              className="flex h-full items-center rounded-md px-3 text-sm font-medium text-foreground transition-all"
                              style={{
                                width: `${Math.max(width, bucket.count > 0 ? 12 : 0)}%`,
                                backgroundColor: "hsl(var(--accent) / 0.24)",
                              }}
                            >
                              {bucket.count > 0 ? bucket.count : ""}
                            </div>
                          </div>
                          <span className="text-right font-mono text-xs text-muted-foreground">{bucket.count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex min-h-40 items-center justify-center rounded-lg border border-dashed border-border/70 bg-muted/30 px-6 text-center text-sm text-muted-foreground">
                    Score more V2 deals to populate workspace score bands.
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.section>
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.1fr_0.9fr]">
          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-border/60 bg-card/80 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="font-display text-xl text-foreground">Deal activity</CardTitle>
                <p className="text-sm text-muted-foreground">Monthly V2 deal volume within the active workspace.</p>
              </CardHeader>
              <CardContent>
                {analytics.monthlyData.length > 0 ? (
                  <div className="flex h-48 items-end gap-3">
                    {analytics.monthlyData.map((month) => {
                      const height = (month.count / analytics.maxMonthlyCount) * 100;
                      return (
                        <div key={month.label} className="flex flex-1 flex-col items-center gap-2">
                          <span className="font-mono text-2xs text-muted-foreground">{month.count}</span>
                          <div className="relative flex h-36 w-full items-end overflow-hidden rounded-t-md rounded-b-sm bg-muted">
                            <div
                              className="w-full rounded-t-md transition-all"
                              style={{ height: `${Math.max(height, 4)}%`, backgroundColor: "hsl(var(--accent))" }}
                            />
                          </div>
                          <span className="eyebrow text-muted-foreground">{month.label}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex min-h-40 items-center justify-center rounded-lg border border-dashed border-border/70 bg-muted/30 px-6 text-center text-sm text-muted-foreground">
                    No V2 deal activity has been recorded in this workspace yet.
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <Card className="border-border/60 bg-card/80 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="font-display text-xl text-foreground">Saved reports</CardTitle>
                <p className="text-sm text-muted-foreground">Report outputs tied to V2 deals in the active workspace.</p>
              </CardHeader>
              <CardContent>
                {savedReports.length > 0 ? (
                  <div className="space-y-2.5">
                    {savedReports.map((report) => (
                      <div key={report.id} className="flex items-start gap-3 rounded-lg border border-border/70 bg-background/70 p-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent/15">
                          <Handshake className="h-4 w-4 text-accent-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-foreground">{report.label}</p>
                          <p className="mt-1 eyebrow text-muted-foreground">
                            {new Date(report.created_at).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {report.report_type === "negotiation_summary" ? "Negotiation summary" : report.report_type.replace(/_/g, " ")}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          {report.ai_content && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => {
                                navigator.clipboard.writeText(report.ai_content || "");
                                toast({ title: "Copied to clipboard" });
                              }}
                            >
                              <Clipboard className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => deleteReport(report.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex min-h-40 items-center justify-center rounded-lg border border-dashed border-border/70 bg-muted/30 px-6 text-center text-sm text-muted-foreground">
                    Saved exports for V2 deals will appear here as they are created.
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.section>
        </div>

        <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="border-border/60 bg-card/80 shadow-sm">
            <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="eyebrow text-muted-foreground">Risk watch</p>
                <div className="mt-1 font-display text-2xl text-foreground">
                  {analytics.dscrBelowOneRate !== null ? `${Math.round(analytics.dscrBelowOneRate)}%` : "—"}
                </div>
                <p className="text-sm text-muted-foreground">of deals with DSCR data are currently below 1.00x.</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate("/deals-v2/my-deals")} className="gap-2 font-display">
                  <ArrowRight className="h-3.5 w-3.5" /> View all V2 deals
                </Button>
                <Button variant="ghost" onClick={() => navigate("/deals-v2/analytics")} className="gap-2 font-display">
                  <Download className="h-3.5 w-3.5" /> Deep analytics
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.section>
      </div>
    </div>
  );
};

export default DealsV2Reports;
